<template>
    <div v-if="canEdit" class="input-group mb-3">
        <input type="text" class="form-control" placeholder="OR NO." v-model="orNo">
        <input placeholder="Date" class="form-control" type="text" v-model="dateInput" @focus="setDateType"
            @blur="setTextType">
        <input type="text" class="form-control" placeholder="AMOUNT" v-model="amount">
    </div>

    <div ref="certificateContainer" class="certificate-container">
        <!-- Decorative border -->
        <div class="certificate-border"></div>

        <!-- Watermark -->
        <img :src="watermarkImage" class="certificate-watermark" alt="Watermark">

        <!-- Certificate content -->
        <div class="certificate-content">
            <!-- Header Logo -->
            <img v-if="currentSoaImage" :src="currentSoaImage" alt="Logo" class="header-logo-small">

            <!-- QR Code -->
            <div class="qr-code-container">
                <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="QR Code" class="qr-code-image" width="100">
            </div>
            <TheSignature :position="JSON.parse(sig.signature_position_json)" :sig="sig" v-for="sig in signatories"
                :key="`sig${sig.official_id}`" />
            <!-- Certificate Body -->
            <div class="certificate-body">
                <center class="certificate-header">
                    <div class="republic-header">
                        Republic of the Philippines <br>
                        Province of Leyte
                    </div>

                    <h4 class="municipality-title">MUNICIPALITY OF PALOMPON</h4>

                    <h1 class="office-title">OFFICE OF THE MUNICIPAL HEALTH</h1>

                    <div class="permit-issued-text">
                        <i>This Sanitary Permit to operate is issued to</i>
                    </div>

                    <h5 class="bin-number">
                        <u><b>BIN: {{ currentApplication?.application?.bin }}</b></u>
                    </h5>

                    <h4 class="business-name">
                        <u>{{ businessName }}</u>
                    </h4>

                    <h4 class="owner-name">
                        <u>{{ ownerFullName }}</u>
                    </h4>

                    <h4 class="business-address">
                        <u>{{ businessAddress }}</u>
                    </h4>

                    <div class="permit-text">
                        <p>
                            This Sanitary Permit is non-transferable and will be revoked for violation of
                            any Sanitary Rule or Regulation. <br>
                            This permit shall be posted in a conspicuous place of the establishment per
                            Chapter III, Section 3 & 4 (Article F) of <br>
                            the Presidential Decree No. 856 (Code of Sanitation of the Philippines) Series
                            of 1995.
                        </p>

                        <p class="issued-date">
                            <b>ISSUED this {{ formatDate(dateIssued) }}</b>
                        </p>

                        <p class="expiry-date">
                            <b>This Permit is valid until {{ formatDate(dateExpire) }}</b>
                        </p>
                    </div>
                </center>

                <!-- Payment Details -->
                <div class="payment-details">
                    <b>OR NO:</b> {{ orNo || 'N/A' }}<br>
                    <b>DATE:</b> {{ formatDate(dateInput) }}<br>
                    <b>AMOUNT:</b> {{ formattedAmount }}<br>
                    <b>PLACE:</b> Palompon, Leyte<br>
                </div>

            </div>
        </div>
    </div>

    <div class="action-buttons">
        <button v-if="canPrint" @click="printAsImage" class="btn btn-primary">
            Print Certificate
        </button>
        <button v-if="canPrint" @click="downloadAsImage" class="btn btn-success">
            Download Certificate
        </button>
        <button v-if="orNo && dateInput && amount && canSaveToIssue" class="btn btn-warning" @click="saveToIssue">
            SAVE TO ISSUE
        </button>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import watermarkImage from '@/assets/img-headers/watermark2.png';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import TheSignature from '../docs/TheSignature.vue';
import signatureService from '@/services/signatureService';
import confirmationService from '@/services/confirmationService';
import certificateService from '@/services/certificateService';
import toastService from '@/services/toastService';
import spinnerService from '@/services/spinnerService';
import Formatters from '@/utils/formatters';
/* eslint-disable */
// Props
//eslint-disable-next-line
const props = defineProps({
    currentApplication: {
        type: Object,
        default: () => ({})
    },
    currentSoa: {
        type: Object,
        default: () => ({})
    },
    userSignatures: {
        type: Object,
        default: () => ({})
    }
});

// Emits
//eslint-disable-next-line
const emit = defineEmits(['print', 'save']);
const signatories = ref([]);
// Reactive data
const orNo = ref('');
const dateInput = ref('');
const amount = ref('');
const dateIssued = ref(new Date().toISOString().split('T')[0]);
const dateExpire = ref(new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]);
const currentCertCode = ref("");
const canSaveToIssue = computed(() => {
    if (!props.currentApplication.application.application_id) return false;

    if (props.currentApplication.issuedCerts.findIndex(x => x.type === currentCertCode.value.toUpperCase()) === -1)
        return true;

    return false;
});

const canPrint = computed(() => {
    if (props.currentApplication.issuedCerts.findIndex(x => x.type === currentCertCode.value.toUpperCase()) > -1)
        return true;

    return false;
});

// Computed properties
const ownerFullName = computed(() => {
    const app = props.currentApplication;
    const firstName = app.permitInfo?.owner_firstname || '';
    const middleName = app.permitInfo?.owner_middlename || '';
    const lastName = app.permitInfo?.owner_lastname || '';

    // Format: FirstName [MiddleInitial.] LastName
    let fullName = firstName.trim();

    // Add middle initial if middle name exists and is not empty/null/NA
    if (middleName &&
        middleName.trim() !== '' &&
        middleName.trim().toLowerCase() !== 'n/a' &&
        middleName.trim().toLowerCase() !== 'null' &&
        middleName.trim().toLowerCase() !== 'none') {

        // Take first character of middle name and add period
        const middleInitial = middleName.trim().charAt(0).toUpperCase() + '.';
        fullName += ' ' + middleInitial;
    }

    // Add last name
    fullName += ' ' + lastName.trim();

    return fullName.trim();
});

const businessName = computed(() => {
    return props.currentApplication?.permitInfo?.business_name ||
        props.currentApplication?.bInfoPermit?.business_name ||
        'N/A';
});

const businessAddress = computed(() => {
    const app = props.currentApplication;
    const parts = [
        app.businessInfo?.operation_building_name,
        app.businessInfo?.operation_block_no_lot_no,
        app.businessInfo?.operation_street,
        app.businessInfo?.operation_subdivision,
        app.businessInfo?.operation_barangay,
        app.businessInfo?.operation_city,
        app.businessInfo?.operation_province
    ].filter(part => part && part !== 'None' && part !== 'N/A' && part !== 'null');
    return parts.join(' ') || 'N/A';
});

const currentSoaImage = computed(() => {
    if (props.currentSoa?.img_url) {
        if (props.currentSoa.img_url.startsWith('http') || props.currentSoa.img_url.startsWith('/')) {
            return props.currentSoa.img_url;
        }
        return `/uploads/${props.currentSoa.img_url}`;
    }
    return null;
});

const sanInspectorSignature = computed(() => {
    const sig = props.userSignatures?.SAN_INSP;
    if (sig?.ds_id && sig?.type) {
        return `ezgovtEngine/uploads/setup/signature/${sig.ds_id}/${sig.type}-sig.png`;
    }
    return null;
});

const mhoSignature = computed(() => {
    const sig = props.userSignatures?.SAN_MHO;
    if (sig?.ds_id && sig?.type) {
        return `ezgovtEngine/uploads/setup/signature/${sig.ds_id}/${sig.type}-sig.png`;
    }
    return null;
});

const formattedAmount = computed(() => {
    if (!amount.value) return '₱0.00';

    const num = parseFloat(amount.value);
    if (isNaN(num)) return amount.value;

    return new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
});

// Methods
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const [month, day, year] = parts;
                return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
            }
            return dateString;
        }

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    } catch (error) {
        return dateString;
    }
};

const setDateType = (event) => {
    event.target.type = 'date';
};

const setTextType = (event) => {
    if (!event.target.value) {
        event.target.type = 'text';
    }
};

const saveToIssue = async () => {
    const confirmed = await confirmationService.show("Are you sure you want to issue certificate?");
    if (!confirmed) return;
    const payload = {
        ref_date: dateInput.value,
        ref_no: orNo.value,
        type: 'SAN',
        amount: parseFloat(amount.value) || 0,
        period: props.currentApplication?.application?.year,
        application_id: props.currentApplication?.application?.application_id
    };
    spinnerService.show();
    const resp = await certificateService.saveToIssue(payload);
    if (resp.success) {
        toastService.success("Certificate issued successfully!");
        emit('save-success');
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();

};

// QR Code functionality
const qrCodeDataUrl = ref('');

const generateQRCodeData = () => {
    const app = props.currentApplication;
    if (!app || !app.application) return null;

    const qrData = {
        type: 'SANITARY_PERMIT',
        bin: app.application.bin || '',
        businessName: businessName.value,
        ownerName: ownerFullName.value,
        issueDate: dateIssued.value,
        expiryDate: dateExpire.value,
        status: 'VALID'
    };

    return JSON.stringify(qrData);
};

const generateQRCode = async () => {
    try {
        const qrData = generateQRCodeData();

        if (!qrData) {
            qrCodeDataUrl.value = '';
            return;
        }

        const url = await QRCode.toDataURL(qrData, {
            width: 100,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            },
            errorCorrectionLevel: 'H'
        });

        qrCodeDataUrl.value = url;

    } catch (error) {
        console.error('Error generating QR code:', error);
        qrCodeDataUrl.value = '';
    }
};

// Printing functionality
const certificateContainer = ref(null);

const printAsImage = async () => {
    if (!certificateContainer.value) return;

    try {
        const loading = showLoading('Generating certificate image...');

        const canvas = await html2canvas(certificateContainer.value, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
            onclone: (clonedDoc) => {
                const images = clonedDoc.querySelectorAll('img');
                return Promise.all(
                    Array.from(images).map(img => {
                        if (img.complete) return Promise.resolve();
                        return new Promise((resolve, reject) => {
                            img.onload = resolve;
                            img.onerror = reject;
                        });
                    })
                );
            }
        });

        const imageData = canvas.toDataURL('image/png', 1.0);
        await printImageData(imageData);
        loading.hide();
    } catch (error) {
        console.error('Error generating certificate image:', error);
        alert('Failed to generate certificate. Please try again.');
    }
};

const downloadAsImage = async () => {
    if (!certificateContainer.value) return;

    try {
        const canvas = await html2canvas(certificateContainer.value, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
        });

        const link = document.createElement('a');
        link.download = `sanitary-permit-${new Date().getTime()}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
    } catch (error) {
        console.error('Error downloading certificate:', error);
    }
};

const printImageData = (imageData) => {
    return new Promise((resolve) => {
        const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Certificate</title>
        <style>
          @page { margin: 0; }
          body { margin: 0; }
          img { width: 100%; height: auto; }
        </style>
      </head>
      <body>
        <img src="${imageData}" 
             onload="setTimeout(() => { window.print(); setTimeout(() => window.close(), 1000); }, 500);"
             onerror="window.close()">
        <script>
          window.onafterprint = () => setTimeout(() => window.close(), 500);
          setTimeout(() => { if(!window.closed) window.close(); }, 10000);
        <\/script>
      </body>
      </html>
    `;

        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const htmlUrl = URL.createObjectURL(htmlBlob);

        const newTab = window.open(htmlUrl, '_blank');

        if (!newTab) {
            const printIframe = document.createElement('iframe');
            printIframe.style.cssText = 'position:fixed;width:0;height:0;border:none;';
            printIframe.srcdoc = htmlContent;
            document.body.appendChild(printIframe);

            printIframe.onload = () => {
                setTimeout(() => {
                    printIframe.contentWindow.print();
                    setTimeout(() => {
                        document.body.removeChild(printIframe);
                        URL.revokeObjectURL(htmlUrl);
                    }, 1000);
                }, 500);
            };
        } else {
            setTimeout(() => URL.revokeObjectURL(htmlUrl), 1000);
        }

        resolve();
    });
};

const showLoading = (message) => {
    const loading = document.createElement('div');
    loading.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      color: white;
      font-size: 18px;
      flex-direction: column;
    ">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div style="margin-top: 20px">${message}</div>
    </div>
  `;
    document.body.appendChild(loading);

    return {
        hide: () => {
            if (loading.parentNode) {
                loading.parentNode.removeChild(loading);
            }
        }
    };
};


const preProcessCertInfo = (certInfo) => {
    if (!certInfo) return;
    orNo.value = certInfo.ref_no;
    dateInput.value = Formatters.formatDateForInput(certInfo.ref_date);
    amount.value = certInfo.amount;
}

const getSignatories = async (code) => {
    currentCertCode.value = code;
    const certInfo = props.currentApplication.issuedCerts.find(x => x.type === code.toUpperCase());
    preProcessCertInfo(certInfo);
    signatories.value = await signatureService.getSignatoriesByCode(code);
}

defineExpose({ getSignatories });

// Initialize QR code and watch for changes
onMounted(() => {
    generateQRCode();
});

watch(() => props.currentApplication, (newApp) => {
    if (!newApp || !newApp.application) {
        orNo.value = '';
        dateInput.value = '';
        amount.value = '';
    }
    generateQRCode();
}, { deep: true });
</script>

<style scoped>
/* Certificate container - fixed width for all screens */
.certificate-container {
    background-color: white;
    padding: 30px 40px;
    width: 800px;
    margin: 0 auto;
    min-height: 1100px;
    box-sizing: border-box;
    position: relative;
    page-break-inside: avoid;
    font-family: 'Times New Roman', Times, serif;
}

/* Certificate content area */
.certificate-content {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 2;
}

/* Certificate border decoration */
.certificate-border {
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    border: 3px double #006400;
    pointer-events: none;
    z-index: 1;
}

/* Watermark */
.certificate-watermark {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 650px;
    height: 650px;
    opacity: 0.5;
    z-index: 0;
    pointer-events: none;
}

/* Header Logo (small, top right) */
.header-logo-small {
    position: absolute;
    right: 40px;
    top: 10px;
    width: 100px;
    height: auto;
    z-index: 3;
}

/* QR Code Container */
.qr-code-container {
    text-align: center;
    margin: 20px 0;
}

.qr-code-image {
    border: 1px solid #ddd;
    padding: 5px;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: inline-block;
}

/* Certificate Body */
.certificate-body {
    position: relative;
    z-index: 2;
}

.certificate-header {
    margin-bottom: 30px;
}

.republic-header {
    font-size: 19px;
    color: #000;
    margin-bottom: 10px;
    line-height: 1.4;
}

.municipality-title {
    font-family: 'Times New Roman', Times, serif;
    font-size: 22px;
    color: #000;
    margin: 10px 0;
    font-weight: bold;
}

.office-title {
    font-family: 'Times New Roman', Times, serif;
    font-size: 26px;
    color: #000;
    margin: 5px 0;
    font-weight: bold;
}

.permit-issued-text {
    font-style: italic;
    font-weight: bold;
    font-size: 17px;
    color: #000;
    margin: 2px 0;
}

.bin-number {
    font-size: 18px;
    color: #000;
    margin: 15px 0;
}

.bin-number u {
    text-decoration: none;
    border-bottom: 2px solid #000;
}

.business-name,
.owner-name,
.business-address {
    font-family: 'Times New Roman', Times, serif;
    color: #000;
    font-weight: bold;
    margin: 10px 0;
    font-size: 20px;
}

.business-name u,
.owner-name u,
.business-address u {
    text-decoration: none;
    border-bottom: 2px solid #000;
    padding-bottom: 2px;
}

.permit-text {
    margin: 20px 0;
    color: #000;
}

.permit-text p {
    font-size: 21px;
    font-weight: bold;
    line-height: 1.4;
    margin: 0px 0;
}

.issued-date,
.expiry-date {
    font-size: 21px;
    color: #000;
    margin: 0px 0;
}

/* Payment Details */
.payment-details {
    padding: 0px 20px;
    color: #000;
    font-size: 18px;
    line-height: 2;
    margin: 0px 0;
    text-align: left;
    ;
}

/* Signatures */
.signatures-row {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    padding: 0 30px;
}

.signature-column {
    flex: 0 0 48%;
}

.signature-container {
    position: relative;
    text-align: center;
    padding-top: 60px;
    min-height: 150px;
}

.inspector-signature {
    padding-top: 40px;
}

.mho-signature {
    padding-top: 65px;
}

.signature-image {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    height: auto;
    opacity: 0.9;
    z-index: 1;
}

.inspector-signature .signature-image {
    width: 90px;
    top: -25px;
    right: 260px;
}

.mho-signature .signature-image {
    width: 150px;
    top: -50px;
    right: 200px;
}

.signatory-name {
    font-family: 'Times New Roman', Times, serif;
    font-size: 17px;
    font-weight: bold;
    color: #000;
    margin-top: 10px;
}

.signatory-title {
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    color: #555;
    margin-top: 5px;
}

/* Input controls */
.input-group {
    gap: 10px;
    margin-bottom: 20px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
}

.input-group .form-control {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    padding: 8px 12px;
}

.action-buttons {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 30px;
}

.action-buttons .btn {
    margin: 0 5px;
}

/* PRINT-ONLY STYLES */
@media print {
    @page {
        size: A4 portrait;
        margin: 0;
    }

    body,
    html {
        margin: 0;
        padding: 0;
        background: white !important;
        width: 210mm;
        min-height: 297mm;
    }

    .certificate-container {
        width: 100% !important;
        max-width: 100% !important;
        min-height: 297mm !important;
        margin: 0 !important;
        padding: 15mm !important;
        border: none !important;
        box-shadow: none !important;
        page-break-after: always;
        break-inside: avoid;
    }

    body * {
        visibility: hidden;
    }

    .certificate-container,
    .certificate-container * {
        visibility: visible;
    }

    .certificate-container {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
    }

    .input-group,
    .action-buttons,
    .btn,
    button {
        display: none !important;
    }

    .certificate-border {
        border: 3px double #006400 !important;
    }

    .certificate-watermark {
        opacity: 0.09 !important;
        width: 500px !important;
        height: 500px !important;
    }

    .republic-header,
    .municipality-title,
    .office-title,
    .permit-issued-text,
    .bin-number,
    .business-name,
    .owner-name,
    .business-address,
    .permit-text,
    .issued-date,
    .expiry-date,
    .payment-details,
    .signatory-name,
    .signatory-title {
        color: #000 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    .permit-text p {
        font-size: 18pt !important;
        line-height: 1.4 !important;
    }

    .signature-image {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
    }
}

/* SCREEN-ONLY STYLES */
@media screen {
    .certificate-container {
        overflow: auto;
        max-height: 90vh;
    }

    .certificate-container::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    .certificate-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
}

@media print and (color) {
    * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}
</style>